import type { Json } from "@/integrations/supabase/types";

export type NotificationType =
  // Task Notifications
  | "TASK_ASSIGNED"
  | "TASK_COMPLETED"
  | "TASK_STATUS_CHANGED"
  | "TASK_DUE_REMINDER"
  | "TASK_OVERDUE"
  | "TASK_COMMENT"
  | "TASK_MENTION"
  | "TASK_PRIORITY_CHANGED"
  | "TASK_ATTACHMENT_ADDED"
  | "SUBTASK_COMPLETED"
  // Community Notifications
  | "POST_LIKED"
  | "POST_COMMENTED"
  | "COMMENT_REPLIED"
  | "COMMUNITY_MENTION"
  // Admin Notifications
  | "ROLE_CHANGED"
  | "SYSTEM_ANNOUNCEMENT"
  | "ADMIN_ACTION"
  // Achievement Notifications
  | "RIZZ_MILESTONE" // @deprecated — use "XP_MILESTONE"
  | "XP_MILESTONE"
  | "STREAK_BONUS"
  | "ACHIEVEMENT_BADGE"
  | "LEADERBOARD_UPDATE"
  // Season Notifications
  | "SEASON_START"
  | "SEASON_END"
  | "SEASON_WEEKLY_UPDATE"
  | "SEASON_RANK_CHANGE"
  // Sales Portal Notifications
  | "SALES_TEAM_INVITE"
  | "SALES_TEAM_JOIN"
  | "SALES_TEAM_ROLE_CHANGE"
  | "COMMISSION_EARNED"
  | "TRANSACTION_APPROVED"
  | "TRANSACTION_REJECTED"
  | "DEAL_CLOSED"
  | "LEAD_ASSIGNED"
  | "TARGET_ACHIEVED"
  | "SALES_RANK_CHANGE"
  | "TEAM_ANNOUNCEMENT"
  // CRM Notifications
  | "CRM_CLIENT_CREATED"
  | "CRM_CLIENT_ASSIGNED"
  | "CRM_CLIENT_STATUS_CHANGED"
  | "CRM_ACTIVITY_LOGGED"
  | "CRM_FOLLOWUP_DUE"
  | "CRM_DEAL_STAGE_CHANGED"
  | "CRM_CONTRACT_SIGNED"
  | "CRM_PAYMENT_RECEIVED"
  | "CRM_NOTE_ADDED"
  // Subscription/Accounting Notifications
  | "SUBSCRIPTION_RENEWAL_DUE"
  | "SUBSCRIPTION_EXPIRED"
  | "SUBSCRIPTION_PAYMENT_FAILED"
  | "SUBSCRIPTION_COST_INCREASE"
  // Cortex Background Job Notifications
  | "CORTEX_JOB_QUEUED"
  | "CORTEX_JOB_COMPLETED"
  | "CORTEX_JOB_FAILED";

export const NOTIFICATION_TYPES = {
  // Task Notifications
  TASK_ASSIGNED: "TASK_ASSIGNED" as const,
  TASK_COMPLETED: "TASK_COMPLETED" as const,
  TASK_STATUS_CHANGED: "TASK_STATUS_CHANGED" as const,
  TASK_DUE_REMINDER: "TASK_DUE_REMINDER" as const,
  TASK_OVERDUE: "TASK_OVERDUE" as const,
  TASK_COMMENT: "TASK_COMMENT" as const,
  TASK_MENTION: "TASK_MENTION" as const,
  TASK_PRIORITY_CHANGED: "TASK_PRIORITY_CHANGED" as const,
  TASK_ATTACHMENT_ADDED: "TASK_ATTACHMENT_ADDED" as const,
  SUBTASK_COMPLETED: "SUBTASK_COMPLETED" as const,
  // Community Notifications
  POST_LIKED: "POST_LIKED" as const,
  POST_COMMENTED: "POST_COMMENTED" as const,
  COMMENT_REPLIED: "COMMENT_REPLIED" as const,
  COMMUNITY_MENTION: "COMMUNITY_MENTION" as const,
  // Admin Notifications
  ROLE_CHANGED: "ROLE_CHANGED" as const,
  SYSTEM_ANNOUNCEMENT: "SYSTEM_ANNOUNCEMENT" as const,
  ADMIN_ACTION: "ADMIN_ACTION" as const,
  // Achievement Notifications
  /** @deprecated Use XP_MILESTONE instead */
  RIZZ_MILESTONE: "RIZZ_MILESTONE" as const,
  XP_MILESTONE: "XP_MILESTONE" as const,
  STREAK_BONUS: "STREAK_BONUS" as const,
  ACHIEVEMENT_BADGE: "ACHIEVEMENT_BADGE" as const,
  LEADERBOARD_UPDATE: "LEADERBOARD_UPDATE" as const,
  // Season Notifications
  SEASON_START: "SEASON_START" as const,
  SEASON_END: "SEASON_END" as const,
  SEASON_WEEKLY_UPDATE: "SEASON_WEEKLY_UPDATE" as const,
  SEASON_RANK_CHANGE: "SEASON_RANK_CHANGE" as const,
  // Sales Portal Notifications
  SALES_TEAM_INVITE: "SALES_TEAM_INVITE" as const,
  SALES_TEAM_JOIN: "SALES_TEAM_JOIN" as const,
  SALES_TEAM_ROLE_CHANGE: "SALES_TEAM_ROLE_CHANGE" as const,
  COMMISSION_EARNED: "COMMISSION_EARNED" as const,
  TRANSACTION_APPROVED: "TRANSACTION_APPROVED" as const,
  TRANSACTION_REJECTED: "TRANSACTION_REJECTED" as const,
  DEAL_CLOSED: "DEAL_CLOSED" as const,
  LEAD_ASSIGNED: "LEAD_ASSIGNED" as const,
  TARGET_ACHIEVED: "TARGET_ACHIEVED" as const,
  SALES_RANK_CHANGE: "SALES_RANK_CHANGE" as const,
  TEAM_ANNOUNCEMENT: "TEAM_ANNOUNCEMENT" as const,
  // CRM Notifications
  CRM_CLIENT_CREATED: "CRM_CLIENT_CREATED" as const,
  CRM_CLIENT_ASSIGNED: "CRM_CLIENT_ASSIGNED" as const,
  CRM_CLIENT_STATUS_CHANGED: "CRM_CLIENT_STATUS_CHANGED" as const,
  CRM_ACTIVITY_LOGGED: "CRM_ACTIVITY_LOGGED" as const,
  CRM_FOLLOWUP_DUE: "CRM_FOLLOWUP_DUE" as const,
  CRM_DEAL_STAGE_CHANGED: "CRM_DEAL_STAGE_CHANGED" as const,
  CRM_CONTRACT_SIGNED: "CRM_CONTRACT_SIGNED" as const,
  CRM_PAYMENT_RECEIVED: "CRM_PAYMENT_RECEIVED" as const,
  CRM_NOTE_ADDED: "CRM_NOTE_ADDED" as const,
  // Subscription/Accounting Notifications
  SUBSCRIPTION_RENEWAL_DUE: "SUBSCRIPTION_RENEWAL_DUE" as const,
  SUBSCRIPTION_EXPIRED: "SUBSCRIPTION_EXPIRED" as const,
  SUBSCRIPTION_PAYMENT_FAILED: "SUBSCRIPTION_PAYMENT_FAILED" as const,
  SUBSCRIPTION_COST_INCREASE: "SUBSCRIPTION_COST_INCREASE" as const,
  // Cortex Background Job Notifications
  CORTEX_JOB_QUEUED: "CORTEX_JOB_QUEUED" as const,
  CORTEX_JOB_COMPLETED: "CORTEX_JOB_COMPLETED" as const,
  CORTEX_JOB_FAILED: "CORTEX_JOB_FAILED" as const,
  // Aliases for backward compatibility
  MENTION: "TASK_MENTION" as const,
} as const;

export interface NotificationActor {
  username: string | null;
  full_name: string | null;
  avatar_url: string | null;
}

export interface Notification {
  id: string;
  user_id: string;
  actor_id: string | null;
  type: string;
  entity_type: string | null;
  entity_id: string | null;
  message_preview: string | null;
  data?: Json;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  needs_multi_channel: boolean | null;
  actor?: NotificationActor;
  title?: string;
}

export interface CreateNotificationData {
  user_id: string;
  actor_id?: string;
  type: NotificationType;
  entity_type?: string;
  entity_id?: string;
  message_preview?: string;
  data?: Json;
  title?: string;
  related_id?: string;
}

// Additional types needed by notificationService
export interface NotificationInsert {
  user_id: string;
  actor_id?: string;
  type: NotificationType;
  entity_type?: string;
  entity_id?: string;
  message_preview?: string;
  data?: Json;
  is_read?: boolean;
}

export interface NotificationUpdate {
  is_read?: boolean;
  read_at?: string;
  message_preview?: string;
  data?: Json;
}

export interface FetchNotificationsOptions {
  page?: number;
  pageSize?: number;
  unreadOnly?: boolean;
  includeRead?: boolean;
  limit?: number;
  offset?: number;
}

export interface NotificationsResponse {
  notifications: Notification[];
  count: number;
  hasMore: boolean;
  error?: Error;
}
